"use client";

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  type Currency,
  defaultCurrencyForLocale,
} from "@/i18n/currency";
import { CURRENCY_COOKIE, type Locale } from "@/i18n/routing";

type CurrencyContextValue = {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
};

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]+)`));
  return match?.[1];
}

function writeCookie(name: string, value: string) {
  if (typeof document === "undefined") return;
  const oneYear = 60 * 60 * 24 * 365;
  document.cookie = `${name}=${value}; path=/; max-age=${oneYear}; samesite=lax`;
}

export function CurrencyProvider({
  locale,
  initialCurrency,
  children,
}: {
  locale: Locale;
  /**
   * Seeded from the SSR cookie (`kessem-currency`) so the first paint already
   * shows the right currency — no client-side flicker after hydration.
   */
  initialCurrency?: Currency;
  children: React.ReactNode;
}) {
  const [currency, setCurrencyState] = useState<Currency>(
    () => initialCurrency ?? defaultCurrencyForLocale(locale),
  );

  // Re-sync from cookie if user changed it in another tab, etc.
  useEffect(() => {
    const stored = readCookie(CURRENCY_COOKIE);
    if (stored === "EUR" || stored === "ILS") {
      setCurrencyState((prev) => (prev === stored ? prev : stored));
    }
  }, []);

  const setCurrency = useCallback((next: Currency) => {
    setCurrencyState(next);
    writeCookie(CURRENCY_COOKIE, next);
  }, []);

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used within CurrencyProvider");
  return ctx;
}
